import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * OVERLAYS — dialog, popover and the surfaces that float above the page.
 *
 * Shared here rather than per-component because a dialog and a popover that
 * disagree about radius, shadow or animation timing look like two products.
 */

/** The dimmed backdrop behind a modal. */
export const overlayBackdropRecipe = cva([
  uiMarker,
  'fixed inset-0 z-50 bg-foreground/40 backdrop-blur-[2px]',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
]);

/**
 * The dialog panel itself.
 *
 * On small screens it becomes a bottom sheet rather than a centred box — a
 * centred modal on a phone leaves the actions in the middle of the screen,
 * away from the thumb.
 */
export const dialogRecipe = cva(
  [
    uiMarker,
    'fixed z-50 flex flex-col bg-card text-card-foreground shadow-e3',
    // No padding and no gap here: each region owns its own inset, which is
    // the only way a header's rule can reach the panel's edges. When the
    // panel held `p-6` the rule stopped 24px short on both sides and the
    // header could not sit flush at the top. Sheet already worked this way.
    'overflow-hidden',
    'border border-border',
    // Capped so a long form scrolls inside the panel instead of running off
    // the screen with its footer unreachable. dvh rather than vh because on
    // mobile Safari vh includes the address bar that is covering the panel.
    'max-h-[90dvh]',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
    // Phone: full-width sheet pinned to the bottom.
    'inset-x-0 bottom-0 rounded-t-xl',
    'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
    // Tablet and up: a centred panel.
    'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2',
    'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl',
    'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0',
    'sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95',
  ],
  {
    variants: {
      size: {
        sm: 'sm:w-full sm:max-w-sm',
        md: 'sm:w-full sm:max-w-lg',
        lg: 'sm:w-full sm:max-w-2xl',
        /** For anything with a table or a long form inside. */
        xl: 'sm:w-full sm:max-w-4xl',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

export type DialogRecipeProps = VariantProps<typeof dialogRecipe>;

/**
 * The dialog's title block.
 *
 * Sticky, on the panel's own background, with a rule under it — which is what
 * the shadcn header it replaced did, and what got dropped on the way into the
 * library. In composed mode the header sits *inside* the scrolling body, so
 * without `sticky` the title of a long form scrolls away and the modal reads
 * as having no header at all. That is exactly how it was reported.
 */
export const dialogHeaderRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    // A row, so the close button is a sibling of the title rather than
    // floating over the panel. Matches sheetHeaderRecipe exactly.
    'flex items-start justify-between gap-4',
    // bg-card, not bg-background: it has to match the panel it sits on, or
    // content scrolling underneath shows through.
    'sticky top-0 z-10 bg-card',
  ],
  {
    variants: {
      /**
       * One inset for the whole panel, set on DialogContent and read by each
       * region. `md` is the default: at 24px a form-heavy dialog reads as
       * mostly margin, and the panel is already floating on a dimmed page —
       * it does not need the extra breathing room a card on a busy screen does.
       */
      padding: {
        sm: 'p-3 pb-2',
        md: 'p-4 pb-3',
        lg: 'p-6 pb-4',
      },
      divided: {
        true: 'border-b border-border',
        false: '',
      },
    },
    compoundVariants: [
      // With a rule under it the header is its own band, so it gets its
      // bottom padding back rather than sitting tight above the body.
      { divided: true, padding: 'sm', class: 'pb-3' },
      { divided: true, padding: 'md', class: 'pb-4' },
      { divided: true, padding: 'lg', class: 'pb-6' },
    ],
    defaultVariants: { padding: 'md', divided: true },
  }
);

export type DialogHeaderRecipeProps = VariantProps<typeof dialogHeaderRecipe>;

/** Footer holding the dialog's actions. Mirrors FormActions on mobile. */
export const dialogFooterRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end',
    'border-t border-border',
  ],
  {
    variants: {
      padding: { sm: 'p-3 pt-2', md: 'p-4 pt-3', lg: 'p-6 pt-4' },
    },
    defaultVariants: { padding: 'md' },
  }
);

/** The scrolling middle of a dialog, between a fixed header and footer. */
export const dialogBodyRecipe = cva(
  [uiMarker, uiMarkerLayout, 'min-w-0 flex-1 min-h-0 overflow-y-auto'],
  {
    variants: {
      padding: { sm: 'p-3', md: 'p-4', lg: 'p-6' },
      /**
       * Sits under a header, so it needs less air above it — the header's own
       * bottom padding and rule are already doing that work. A dialog with no
       * header keeps the full inset instead of starting 8px too high.
       */
      headed: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Under a header the rule and its padding already give the content air.
      { headed: true, padding: 'sm', class: 'pt-2' },
      { headed: true, padding: 'md', class: 'pt-3' },
      { headed: true, padding: 'lg', class: 'pt-4' },
    ],
    defaultVariants: { padding: 'md', headed: false },
  }
);

/** Popover / dropdown surface. Quieter than a dialog — no backdrop. */
export const popoverRecipe = cva(
  [
    uiMarker,
    'z-50 rounded-md border border-border bg-popover text-popover-foreground shadow-e3',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  ],
  {
    variants: {
      padding: {
        none: '',
        sm: 'p-2',
        md: 'p-4',
      },
    },
    defaultVariants: { padding: 'md' },
  }
);

/**
 * Tooltip bubble. Inverted against the page — dark on light, light on dark —
 * so it reads as an annotation floating over the UI rather than another panel
 * of it, which is what separates it from a popover at a glance.
 */
export const tooltipRecipe = cva([
  uiMarker,
  'z-50 max-w-xs rounded-md bg-foreground px-2.5 py-1.5 text-caption text-background shadow-e2',
  'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=delayed-open]:zoom-in-95',
]);
